import React, { useState, useEffect, useCallback } from 'react';
import { Users, DollarSign, Receipt, Truck, ShieldAlert, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  getStock, getClientes, createCliente, updateCliente,
  getPrecios, updatePreciosBulk, getFacturas, createFactura, updateFactura, anularFactura,
  getEmpresaConfig, getInventarioPorLote,
} from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { exportToExcel, exportFacturaToPDF } from '../lib/exportUtils';

import { FacturasTable } from './despacho/FacturasTable';
import { FacturaDialog } from './despacho/FacturaDialog';
import { FacturaDetailDialog } from './despacho/FacturaDetailDialog';
import { ClienteDialog } from './despacho/ClienteDialog';
import { ClientesTab } from './despacho/ClientesTab';
import { PreciosDialog } from './despacho/PreciosDialog';

const INITIAL_ITEM = { tipo_huevo: '', lote_id: '', cantidad: '', precio_unitario: '' };

export default function Despacho() {
  const [stock, setStock] = useState({});
  const [stockPorLote, setStockPorLote] = useState({}); // { tipo: [{lote_id, lote_codigo, disponible, ...}] }
  const [clientes, setClientes] = useState([]);
  const [precios, setPrecios] = useState({});
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Factura dialog
  const [facturaOpen, setFacturaOpen] = useState(false);
  const [facturaItems, setFacturaItems] = useState([{ ...INITIAL_ITEM }]);
  const [facturaRotos, setFacturaRotos] = useState([]);
  const [facturaClienteId, setFacturaClienteId] = useState('');
  const [facturaFecha, setFacturaFecha] = useState(new Date().toISOString().split('T')[0]);
  const [facturaObs, setFacturaObs] = useState('');
  const [savingFactura, setSavingFactura] = useState(false);
  const [editingFacturaId, setEditingFacturaId] = useState(null);

  // Delete factura confirm
  const [deletingFactura, setDeletingFactura] = useState(null);
  const [deleteFacturaForce, setDeleteFacturaForce] = useState(false);
  const [deletingFacturaInProgress, setDeletingFacturaInProgress] = useState(false);

  // Modal: Conteo Físico Posterior detectado al editar factura
  const [conteoPosteriorDialog, setConteoPosteriorDialog] = useState({
    open: false,
    message: '',
    conteoFecha: '',
    facturaFecha: '',
    numeroFactura: '',
    pendingPayload: null,
    pin: '',
    ajustarInventario: false,
    submitting: false,
  });

  // Búsqueda
  const [searchFactura, setSearchFactura] = useState('');

  // Cliente dialog
  const [clienteOpen, setClienteOpen] = useState(false);
  const [clienteForm, setClienteForm] = useState({ nombre: '', telefono: '', direccion: '', dias_credito: 0 });
  const [editingClienteId, setEditingClienteId] = useState(null);
  const [savingCliente, setSavingCliente] = useState(false);

  // Precios dialog
  const [preciosOpen, setPreciosOpen] = useState(false);
  const [preciosForm, setPreciosForm] = useState({});
  const [savingPrecios, setSavingPrecios] = useState(false);

  // Detalle factura
  const [detalleFactura, setDetalleFactura] = useState(null);
  const [empresaCfg, setEmpresaCfg] = useState({ rnc: '131-84645-9', nombre: 'AVIPORK' });

  // ============================================================
  // Data load
  // ============================================================
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [stockRes, clientesRes, preciosRes, facturasRes, porLoteRes] = await Promise.all([
        getStock(),
        getClientes(),
        getPrecios(),
        getFacturas({ limit: 2000 }),
        getInventarioPorLote().catch(() => ({ data: { por_tipo: {} } })),
      ]);
      setStock(stockRes.data.stock);
      setClientes(clientesRes.data);
      setPrecios(preciosRes.data);
      setFacturas(facturasRes.data);
      setStockPorLote(porLoteRes.data?.por_tipo || {});
    } catch (err) {
      toast.error('Error cargando datos de despacho');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Cargar config de empresa (RNC, nombre) una sola vez al montar
  useEffect(() => {
    getEmpresaConfig()
      .then((res) => setEmpresaCfg(res.data))
      .catch(() => { /* fallback al default ya en state */ });
  }, []);

  // ============================================================
  // Factura handlers
  // ============================================================
  const openNewFactura = () => {
    setEditingFacturaId(null);
    setFacturaItems([{ ...INITIAL_ITEM }]);
    setFacturaRotos([]);
    setFacturaClienteId('');
    setFacturaFecha(new Date().toISOString().split('T')[0]);
    setFacturaObs('');
    setFacturaOpen(true);
  };

  const openEditFactura = (fac) => {
    setEditingFacturaId(fac.id || fac._id);
    const items = (fac.items || []).map((it) => ({
      tipo_huevo: it.tipo_huevo || '',
      lote_id: it.lote_id || '',
      cantidad: String(it.cantidad ?? ''),
      precio_unitario: String(it.precio_unitario ?? ''),
    }));
    setFacturaItems(items.length ? items : [{ ...INITIAL_ITEM }]);
    setFacturaRotos((fac.huevos_rotos || []).map((r) => ({
      tipo_huevo: r.tipo_huevo || '',
      lote_id: r.lote_id || '',
      cantidad: String(r.cantidad_rota ?? r.cantidad ?? ''),
    })));
    setFacturaClienteId(fac.cliente_id || '');
    setFacturaFecha(fac.fecha?.split('T')[0] || new Date().toISOString().split('T')[0]);
    setFacturaObs(fac.observaciones || '');
    setFacturaOpen(true);
  };

  const addFacturaItem = () => setFacturaItems((prev) => [...prev, { ...INITIAL_ITEM }]);
  const removeFacturaItem = (index) => setFacturaItems((prev) => (
    prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)
  ));
  const updateFacturaItem = (index, field, value) => {
    setFacturaItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'tipo_huevo' && value && precios[value]) {
        updated[index].precio_unitario = String(precios[value]);
      }
      // Cambiar tipo resetea el lote seleccionado (los lotes son por tipo)
      if (field === 'tipo_huevo') {
        updated[index].lote_id = '';
      }
      return updated;
    });
  };

  const addRoto = () => setFacturaRotos((prev) => [...prev, { tipo_huevo: '', lote_id: '', cantidad: '' }]);
  const removeRoto = (index) => setFacturaRotos((prev) => prev.filter((_, i) => i !== index));
  const updateRoto = (index, field, value) => {
    setFacturaRotos((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'tipo_huevo') {
        updated[index].lote_id = '';
      }
      return updated;
    });
  };

  const exportarFacturaPDF = (factura, mode = 'save', variant = 'cliente') => {
    const cliente = clientes.find((c) => (c.id || c._id) === factura.cliente_id) || null;
    const ok = exportFacturaToPDF(factura, cliente, mode, variant, empresaCfg);
    const variantTxt = variant === 'interna' ? 'original' : 'cliente';
    if (ok === 'saved-fallback') {
      toast.warning('La ventana emergente fue bloqueada. Se descargó el PDF en su lugar.');
    } else if (ok) {
      toast.success(mode === 'print' ? `Abriendo impresión (${variantTxt})...` : `PDF (${variantTxt}) descargado`);
    } else {
      toast.error('No se pudo generar el PDF');
    }
  };

  const handleSaveFactura = async () => {
    if (!facturaClienteId) { toast.error('Seleccione un cliente'); return; }
    if (!facturaFecha) { toast.error('Seleccione una fecha'); return; }

    const validItems = facturaItems.filter((item) => item.tipo_huevo && parseInt(item.cantidad, 10) > 0);
    if (validItems.length === 0) {
      toast.error('Agregue al menos un tipo de huevo con cantidad');
      return;
    }

    // Validar que TODOS los items tengan un lote seleccionado
    const sinLote = validItems.filter((item) => !item.lote_id);
    if (sinLote.length > 0) {
      const nombres = sinLote.map((i) => i.tipo_huevo).join(', ');
      toast.error(`Seleccione un lote para: ${nombres}`);
      return;
    }

    // Validar cantidad ≤ disponible por (tipo, lote)
    // Considerar múltiples líneas del mismo (tipo, lote) sumadas
    const demandaPorTipoLote = {};
    for (const item of validItems) {
      const key = `${item.tipo_huevo}||${item.lote_id}`;
      demandaPorTipoLote[key] = (demandaPorTipoLote[key] || 0) + (parseInt(item.cantidad, 10) || 0);
    }
    // Sumar también rotos con lote
    const rotosConLote = facturaRotos.filter((r) => r.tipo_huevo && parseInt(r.cantidad, 10) > 0 && r.lote_id);
    for (const roto of rotosConLote) {
      const key = `${roto.tipo_huevo}||${roto.lote_id}`;
      demandaPorTipoLote[key] = (demandaPorTipoLote[key] || 0) + (parseInt(roto.cantidad, 10) || 0);
    }
    const excesos = [];
    for (const [key, demanda] of Object.entries(demandaPorTipoLote)) {
      const [tipo, loteId] = key.split('||');
      const lotesTipo = stockPorLote[tipo] || stockPorLote[tipo === 'pequeño' ? 'pequeno' : tipo] || [];
      const lote = lotesTipo.find((l) => l.lote_id === loteId);
      const disponible = Number(lote?.disponible || 0);
      // En edición, sumamos las cantidades ya asignadas a esta factura para el mismo (tipo, lote)
      // ya que el backend las restará antes de validar. Aquí replicamos ese comportamiento.
      let asignadoAEstaFactura = 0;
      if (editingFacturaId) {
        const factOrig = facturas.find((f) => (f.id || f._id) === editingFacturaId);
        (factOrig?.items || []).forEach((it) => {
          if (it.tipo_huevo === tipo && it.lote_id === loteId) {
            asignadoAEstaFactura += Number(it.cantidad || 0);
          }
        });
        (factOrig?.huevos_rotos || []).forEach((r) => {
          if (r.tipo_huevo === tipo && r.lote_id === loteId) {
            asignadoAEstaFactura += Number(r.cantidad_rota || 0);
          }
        });
      }
      const disponibleEfectivo = disponible + asignadoAEstaFactura;
      if (demanda > disponibleEfectivo) {
        excesos.push({ tipo, loteCodigo: lote?.lote_codigo || loteId.slice(0, 6), demanda, disponible: disponibleEfectivo });
      }
    }
    if (excesos.length > 0) {
      const msg = excesos.map((e) =>
        `${e.tipo} ${/^lote\b/i.test(String(e.loteCodigo || '')) ? e.loteCodigo : 'Lote ' + e.loteCodigo}: pide ${e.demanda.toLocaleString('en-US')} pero solo hay ${e.disponible.toLocaleString('en-US')}`,
      ).join(' · ');
      toast.error(`Stock insuficiente por lote: ${msg}`, { duration: 8000 });
      return;
    }

    // Validar que TODOS los items tengan precio > 0. No permitimos facturar
    // en 0.00: cada tipo de huevo debe tener un precio unitario explícito.
    const sinPrecio = validItems.filter((item) => {
      const p = parseFloat(item.precio_unitario);
      return !p || isNaN(p) || p <= 0;
    });
    if (sinPrecio.length > 0) {
      const nombres = sinPrecio.map((i) => i.tipo_huevo).join(', ');
      toast.error(
        `Debe asignar un precio mayor a 0 para: ${nombres}. Configure precios en "Precios de Venta".`,
      );
      return;
    }

    setSavingFactura(true);
    try {
      const payload = {
        cliente_id: facturaClienteId,
        fecha: facturaFecha,
        items: validItems.map((item) => {
          const lotesTipo = stockPorLote[item.tipo_huevo] || stockPorLote[item.tipo_huevo === 'pequeño' ? 'pequeno' : item.tipo_huevo] || [];
          const loteFound = lotesTipo.find((l) => l.lote_id === item.lote_id);
          return {
            tipo_huevo: item.tipo_huevo,
            cantidad: parseInt(item.cantidad, 10),
            precio_unitario: parseFloat(item.precio_unitario) || 0,
            lote_id: item.lote_id || null,
            lote_codigo: loteFound?.lote_codigo || null,
          };
        }),
        huevos_rotos: facturaRotos
          .filter((r) => r.tipo_huevo && parseInt(r.cantidad, 10) > 0)
          .map((r) => {
            const lotesTipo = stockPorLote[r.tipo_huevo] || stockPorLote[r.tipo_huevo === 'pequeño' ? 'pequeno' : r.tipo_huevo] || [];
            const loteFound = lotesTipo.find((l) => l.lote_id === r.lote_id);
            return {
              tipo_huevo: r.tipo_huevo,
              cantidad_rota: parseInt(r.cantidad, 10),
              lote_id: r.lote_id || null,
              lote_codigo: loteFound?.lote_codigo || null,
            };
          }),
        observaciones: facturaObs,
      };

      if (editingFacturaId) {
        try {
          const res = await updateFactura(editingFacturaId, payload);
          toast.success(`Factura ${res.data.numero_factura} actualizada`);
        } catch (err) {
          // Conteo físico posterior detectado por el backend (HTTP 409)
          const status = err.response?.status;
          const detail = err.response?.data?.detail;
          if (status === 409 && detail && typeof detail === 'object' && detail.error === 'conteo_posterior_existente') {
            setConteoPosteriorDialog({
              open: true,
              message: detail.message || '',
              conteoFecha: detail.conteo_fecha || '',
              facturaFecha: detail.factura_fecha || '',
              numeroFactura: detail.numero_factura || '',
              pendingPayload: payload,
              pin: '',
              ajustarInventario: false,
              submitting: false,
            });
            setSavingFactura(false);
            return;
          }
          // Stock por lote insuficiente (HTTP 409)
          if (status === 409 && detail && typeof detail === 'object' && detail.error === 'stock_lote_insuficiente') {
            toast.error(detail.message || 'Stock por lote insuficiente', { duration: 10000 });
            setSavingFactura(false);
            return;
          }
          throw err;
        }
      } else {
        try {
          const res = await createFactura(payload);
          toast.success(`Factura ${res.data.numero_factura} creada exitosamente`);
          try {
            exportarFacturaPDF(res.data, 'print', 'cliente');
          } catch (printErr) {
            console.warn('Error al generar PDF de impresión:', printErr);
          }
        } catch (err) {
          const status = err.response?.status;
          const detail = err.response?.data?.detail;
          if (status === 409 && detail && typeof detail === 'object' && detail.error === 'stock_lote_insuficiente') {
            toast.error(detail.message || 'Stock por lote insuficiente', { duration: 10000 });
            setSavingFactura(false);
            return;
          }
          throw err;
        }
      }
      setFacturaOpen(false);
      setEditingFacturaId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando factura');
    } finally {
      setSavingFactura(false);
    }
  };

  const handleConfirmDeleteFactura = async () => {
    if (!deletingFactura) return;
    setDeletingFacturaInProgress(true);
    try {
      const id = deletingFactura.id || deletingFactura._id;
      await anularFactura(id);
      toast.success('Conduce anulado. Inventario revertido y pagos asociados eliminados.');
      setDeletingFactura(null);
      setDeleteFacturaForce(false);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error anulando conduce';
      toast.error(detail, { duration: 6000 });
    } finally {
      setDeletingFacturaInProgress(false);
    }
  };

  const handleConfirmConteoPosteriorFactura = async () => {
    const { pendingPayload, pin, ajustarInventario } = conteoPosteriorDialog;
    const codigo = (pin || '').trim();
    if (codigo.length < 4) {
      toast.error('Ingrese el código de Super Usuario');
      return;
    }
    if (!pendingPayload || !editingFacturaId) {
      toast.error('No hay datos pendientes para actualizar');
      setConteoPosteriorDialog((p) => ({ ...p, open: false }));
      return;
    }
    setConteoPosteriorDialog((p) => ({ ...p, submitting: true }));
    try {
      const res = await updateFactura(editingFacturaId, pendingPayload, {
        confirmar_con_conteo_posterior: true,
        ajustar_inventario: ajustarInventario,
        super_codigo: codigo,
      });
      toast.success(
        ajustarInventario
          ? `Factura ${res.data.numero_factura} actualizada — inventario ajustado (verifique conteos)`
          : `Factura ${res.data.numero_factura} corregida sin tocar inventario`,
      );
      setConteoPosteriorDialog({
        open: false, message: '', conteoFecha: '', facturaFecha: '', numeroFactura: '',
        pendingPayload: null, pin: '', ajustarInventario: false, submitting: false,
      });
      setFacturaOpen(false);
      setEditingFacturaId(null);
      loadData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Error actualizando factura');
      toast.error(msg);
      setConteoPosteriorDialog((p) => ({ ...p, submitting: false }));
    }
  };

  // ============================================================
  // Cliente / Precios
  // ============================================================
  const openNewCliente = () => {
    setEditingClienteId(null);
    setClienteForm({ nombre: '', telefono: '', direccion: '', dias_credito: 0 });
    setClienteOpen(true);
  };

  const openEditCliente = (cli) => {
    setEditingClienteId(cli.id || cli._id);
    setClienteForm({
      nombre: cli.nombre || '',
      telefono: cli.telefono || '',
      direccion: cli.direccion || '',
      dias_credito: Number(cli.dias_credito || 0),
    });
    setClienteOpen(true);
  };

  const handleSaveCliente = async () => {
    if (!clienteForm.nombre || !clienteForm.nombre.trim()) {
      toast.error('Ingrese el nombre del cliente');
      return;
    }
    setSavingCliente(true);
    try {
      const payload = {
        nombre: clienteForm.nombre.trim(),
        telefono: clienteForm.telefono?.trim() || '',
        direccion: clienteForm.direccion?.trim() || '',
        dias_credito: Number(clienteForm.dias_credito) || 0,
      };
      if (editingClienteId) {
        await updateCliente(editingClienteId, payload);
        toast.success('Cliente actualizado');
      } else {
        await createCliente(payload);
        toast.success('Cliente creado exitosamente');
      }
      setClienteOpen(false);
      setEditingClienteId(null);
      setClienteForm({ nombre: '', telefono: '', direccion: '', dias_credito: 0 });
      const res = await getClientes();
      setClientes(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error guardando cliente');
    } finally {
      setSavingCliente(false);
    }
  };

  const openPrecios = () => {
    setPreciosForm({ ...precios });
    setPreciosOpen(true);
  };

  const handleSavePrecios = async () => {
    setSavingPrecios(true);
    try {
      await updatePreciosBulk(preciosForm);
      toast.success('Precios actualizados');
      setPreciosOpen(false);
      const res = await getPrecios();
      setPrecios(res.data);
    } catch (err) {
      toast.error('Error guardando precios');
    } finally {
      setSavingPrecios(false);
    }
  };

  // ============================================================
  // Derivados
  // ============================================================

  // Extrae el número correlativo de un "FAC-0013" -> 13. Devuelve -1 si no
  // se puede parsear, para que esos casos queden al inicio.
  const _facNum = (s) => {
    const m = String(s || '').match(/(\d+)/);
    return m ? parseInt(m[1], 10) : -1;
  };

  const facturasFiltradas = facturas
    .filter((f) => {
      if (!searchFactura.trim()) return true;
      const term = searchFactura.toLowerCase().trim();
      return (
        (f.numero_factura || '').toLowerCase().includes(term) ||
        (f.cliente_nombre || '').toLowerCase().includes(term)
      );
    })
    // Orden descendente por número de conduce (más reciente arriba),
    // incluyendo anuladas y placeholders, para que sea fácil ver lo último.
    .sort((a, b) => _facNum(b.numero_factura) - _facNum(a.numero_factura));

  const exportarFacturasExcel = () => {
    const ok = exportToExcel(
      facturasFiltradas,
      [
        { label: 'N° Factura', key: 'numero_factura' },
        { label: 'Fecha', accessor: (f) => formatDate(f.fecha) },
        { label: 'Cliente', key: 'cliente_nombre' },
        { label: 'Huevos', key: 'total_huevos' },
        { label: 'Rotos', key: 'total_rotos' },
        { label: 'Total', accessor: (f) => Number(f.total_monto || 0).toFixed(2) },
        { label: 'Saldo', accessor: (f) => Number(f.saldo || 0).toFixed(2) },
        { label: 'Estado', key: 'estado_pago' },
        { label: 'Vencimiento', accessor: (f) => formatDate(f.fecha_vencimiento) },
      ],
      'facturas',
      'Facturas',
    );
    if (!ok) toast.error('No hay facturas para exportar');
    else toast.success('Excel descargado');
  };

  const facturaTotalHuevos = facturaItems.reduce(
    (sum, item) => sum + (parseInt(item.cantidad, 10) || 0), 0,
  );
  const facturaTotalRotos = facturaRotos.reduce(
    (sum, r) => sum + (parseInt(r.cantidad, 10) || 0), 0,
  );
  const facturaTotalMonto = facturaItems.reduce((sum, item) => {
    const cant = parseInt(item.cantidad, 10) || 0;
    const precio = parseFloat(item.precio_unitario) || 0;
    return sum + cant * precio;
  }, 0);

  // ============================================================
  // Render
  // ============================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const deletingHasPagos =
    deletingFactura?.estado_pago === 'Parcial' || deletingFactura?.estado_pago === 'Pagada';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2
            className="text-2xl font-bold tracking-tight flex items-center gap-2"
            style={{ fontFamily: 'Space Grotesk' }}
          >
            <Truck className="w-6 h-6" /> Despacho
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Gestiona facturación, clientes y precios de venta
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={openPrecios} data-testid="btn-precios">
            <DollarSign className="w-4 h-4 mr-2" /> Precios
          </Button>
          <Button variant="outline" onClick={openNewCliente} data-testid="btn-nuevo-cliente">
            <Users className="w-4 h-4 mr-2" /> Nuevo Cliente
          </Button>
          <Button onClick={openNewFactura} data-testid="btn-nueva-factura">
            <Receipt className="w-4 h-4 mr-2" /> Nuevo Conduce
          </Button>
        </div>
      </div>

      <Tabs defaultValue="facturas">
        <TabsList>
          <TabsTrigger value="facturas" className="gap-2">
            <Receipt className="w-4 h-4" /> Historial de Conduces
          </TabsTrigger>
          <TabsTrigger value="clientes" className="gap-2" data-testid="tab-clientes-despacho">
            <Users className="w-4 h-4" /> Clientes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="facturas">
          <FacturasTable
            facturas={facturas}
            facturasFiltradas={facturasFiltradas}
            searchFactura={searchFactura}
            setSearchFactura={setSearchFactura}
            onExportExcel={exportarFacturasExcel}
            onVerDetalle={setDetalleFactura}
            onEditFactura={openEditFactura}
            onAnularFactura={(fac) => { setDeletingFactura(fac); setDeleteFacturaForce(false); }}
            onExportPDF={exportarFacturaPDF}
          />
        </TabsContent>

        <TabsContent value="clientes">
          <ClientesTab
            clientes={clientes}
            onEdit={openEditCliente}
            onCreate={openNewCliente}
          />
        </TabsContent>
      </Tabs>

      <FacturaDialog
        open={facturaOpen}
        onOpenChange={(o) => { setFacturaOpen(o); if (!o) setEditingFacturaId(null); }}
        editing={!!editingFacturaId}
        clientes={clientes}
        stock={stock}
        stockPorLote={stockPorLote}
        clienteId={facturaClienteId}
        setClienteId={setFacturaClienteId}
        onOpenNuevoCliente={openNewCliente}
        fecha={facturaFecha}
        setFecha={setFacturaFecha}
        items={facturaItems}
        addItem={addFacturaItem}
        removeItem={removeFacturaItem}
        updateItem={updateFacturaItem}
        rotos={facturaRotos}
        addRoto={addRoto}
        removeRoto={removeRoto}
        updateRoto={updateRoto}
        obs={facturaObs}
        setObs={setFacturaObs}
        totalHuevos={facturaTotalHuevos}
        totalRotos={facturaTotalRotos}
        totalMonto={facturaTotalMonto}
        saving={savingFactura}
        onSave={handleSaveFactura}
      />

      <FacturaDetailDialog
        factura={detalleFactura}
        empresa={empresaCfg}
        onClose={() => setDetalleFactura(null)}
        onExportPDF={exportarFacturaPDF}
      />

      <ClienteDialog
        open={clienteOpen}
        onOpenChange={(o) => { setClienteOpen(o); if (!o) setEditingClienteId(null); }}
        editing={!!editingClienteId}
        form={clienteForm}
        setForm={setClienteForm}
        saving={savingCliente}
        onSave={handleSaveCliente}
      />

      <PreciosDialog
        open={preciosOpen}
        onOpenChange={setPreciosOpen}
        form={preciosForm}
        setForm={setPreciosForm}
        saving={savingPrecios}
        onSave={handleSavePrecios}
      />

      {/* Confirmar anular conduce */}
      <AlertDialog
        open={!!deletingFactura}
        onOpenChange={(o) => { if (!o) { setDeletingFactura(null); setDeleteFacturaForce(false); } }}
      >
        <AlertDialogContent data-testid="confirmar-anular-factura">
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Anular conduce {deletingFactura?.numero_factura}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              El conduce quedará marcado como <strong>Anulada</strong> y se
              {' '}<strong>devolverán {deletingFactura?.total_huevos || 0} huevos al inventario</strong>
              {deletingFactura?.total_rotos > 0 && ` (incluyendo ${deletingFactura.total_rotos} rotos)`}.
              {deletingHasPagos && (
                <span className="block mt-2 text-destructive font-medium">
                  ⚠️ También se eliminarán los pagos asociados ({deletingFactura?.estado_pago}).
                </span>
              )}
              <span className="block mt-2 text-xs text-muted-foreground">
                El número <strong>{deletingFactura?.numero_factura}</strong> NO se borra — queda en
                el historial para no crear huecos en la secuencia.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingFacturaInProgress}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteFactura}
              disabled={deletingFacturaInProgress}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirmar-anular-factura"
            >
              {deletingFacturaInProgress ? 'Anulando...' : 'Anular Conduce'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ==== Modal: Conteo Físico Posterior detectado al editar factura ==== */}
      <Dialog
        open={conteoPosteriorDialog.open}
        onOpenChange={(o) => {
          if (!o && !conteoPosteriorDialog.submitting) {
            setConteoPosteriorDialog((p) => ({ ...p, open: false, pin: '', pendingPayload: null, ajustarInventario: false }));
          }
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="modal-conteo-posterior-factura">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400" style={{ fontFamily: 'Space Grotesk' }}>
              <ShieldAlert className="w-5 h-5" />
              Existe un conteo físico posterior
            </DialogTitle>
            <DialogDescription className="pt-1">
              Estás editando una factura cuya fecha ya está cubierta por un conteo físico. Elige cómo proceder.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-md p-3">
              <div>
                <div className="text-amber-900/70 dark:text-amber-300/70 font-medium">Factura</div>
                <div className="font-semibold text-amber-900 dark:text-amber-100">
                  {conteoPosteriorDialog.numeroFactura || '—'}
                </div>
              </div>
              <div>
                <div className="text-amber-900/70 dark:text-amber-300/70 font-medium">Fecha factura</div>
                <div className="font-semibold text-amber-900 dark:text-amber-100">{conteoPosteriorDialog.facturaFecha || '—'}</div>
              </div>
              <div>
                <div className="text-amber-900/70 dark:text-amber-300/70 font-medium">Conteo físico</div>
                <div className="font-semibold text-amber-900 dark:text-amber-100">{conteoPosteriorDialog.conteoFecha || '—'}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">¿Qué querés hacer?</Label>
              <div className="space-y-2">
                <label
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    !conteoPosteriorDialog.ajustarInventario
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="conteo-posterior-fact-modo"
                    checked={!conteoPosteriorDialog.ajustarInventario}
                    onChange={() => setConteoPosteriorDialog((p) => ({ ...p, ajustarInventario: false }))}
                    className="mt-1 accent-primary"
                    data-testid="radio-fact-solo-corregir"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Corregir SOLO la factura (recomendado)</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Actualiza los items/monto sin tocar el stock actual. Ideal si el conteo físico ya reajustó el inventario.
                    </div>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                    conteoPosteriorDialog.ajustarInventario
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : 'border-border hover:bg-muted/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="conteo-posterior-fact-modo"
                    checked={conteoPosteriorDialog.ajustarInventario}
                    onChange={() => setConteoPosteriorDialog((p) => ({ ...p, ajustarInventario: true }))}
                    className="mt-1 accent-red-500"
                    data-testid="radio-fact-corregir-ajustar"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-red-700 dark:text-red-400">Corregir Y ajustar inventario</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Aplica la diferencia al stock. Puede descuadrar el inventario si el conteo físico ya lo corrigió.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <Label htmlFor="conteo-posterior-fact-pin" className="flex items-center gap-1.5 text-sm">
                <KeyRound className="w-4 h-4" />
                Código de Super Usuario
              </Label>
              <Input
                id="conteo-posterior-fact-pin"
                type="password"
                value={conteoPosteriorDialog.pin}
                onChange={(e) => setConteoPosteriorDialog((p) => ({ ...p, pin: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !conteoPosteriorDialog.submitting && conteoPosteriorDialog.pin.trim().length >= 4) {
                    handleConfirmConteoPosteriorFactura();
                  }
                }}
                placeholder="Ingrese el código"
                className="h-10 mt-1.5 font-mono"
                autoFocus
                data-testid="input-pin-conteo-posterior-factura"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConteoPosteriorDialog((p) => ({ ...p, open: false, pin: '', pendingPayload: null, ajustarInventario: false }))}
              disabled={conteoPosteriorDialog.submitting}
              data-testid="btn-cancelar-conteo-posterior-factura"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmConteoPosteriorFactura}
              disabled={conteoPosteriorDialog.submitting || conteoPosteriorDialog.pin.trim().length < 4}
              variant={conteoPosteriorDialog.ajustarInventario ? 'destructive' : 'default'}
              data-testid="btn-confirmar-conteo-posterior-factura"
            >
              {conteoPosteriorDialog.submitting ? 'Guardando…' : (
                conteoPosteriorDialog.ajustarInventario ? 'Editar y ajustar inventario' : 'Editar solo la factura'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
